import React from 'react';
import ButtonTableAction from '../../../../components/buttons/ButtonTableAction';
import DefaultDataTable from '../../../../components/default/DefaultDataTable';
import { ServiceDataForm, ServiceFilters } from '../../../../app/models/Service';
import useServiceService from '../../../../app/services/hooks/useServiceService';
import ServiceFilter from './ServiceFilter';

interface Props {
    services: ServiceDataForm[];
    totalRows: number;
    loading?: boolean;
    edit?: (id: number) => void;
    destroy?: (id: number) => void;
    filter: ServiceFilters;
    setFilter: (filter: ServiceFilters) => void;
    callbackSuccess: () => void;
}

const ServiceSwitchChange: React.FC<{
    checkValue?: boolean;
    serviceId: number;
    callbackSuccess: () => void;
}> = ({ checkValue, serviceId, callbackSuccess }) => {
    const { activeService } = useServiceService();
    return (
        <div className="form-check form-switch">
            <input
                type="checkbox"
                className="form-check-input"
                checked={!!checkValue}
                onChange={(e) => {
                    activeService(serviceId, e.target.checked, {
                        onSuccess: () => callbackSuccess(),
                        onError: () => callbackSuccess()
                    });
                }}
            />
        </div>
    );
};

const ServiceDataTable = ({
    services,
    totalRows,
    loading = false,
    edit,
    destroy,
    filter,
    setFilter,
    callbackSuccess
}: Props) => {
    const columns = [
        {
            name: 'Id',
            selector: (row: any) => row.id,
            sortable: true,
            sortField: 'id',
            width: '80px',
            cell: (row: ServiceDataForm) => <span>{row.id}</span>
        },
        {
            name: 'Nombre',
            selector: (row: any) => row.name,
            sortable: true,
            sortField: 'name',
            cell: (row: ServiceDataForm) => <span>{row.name}</span>
        },
        {
            name: 'Sector',
            selector: (row: any) => row.sector,
            sortable: false,
            cell: (row: ServiceDataForm) => <span>{row.sector ?? '-'}</span>
        },
        {
            name: 'Categoría',
            selector: (row: any) => row.item_category,
            sortable: false,
            cell: (row: ServiceDataForm) => <span>{row.item_category ?? '-'}</span>
        },
        {
            name: 'Unidad',
            selector: (row: any) => row.unit_symbol ?? row.unit,
            sortable: false,
            cell: (row: ServiceDataForm) => (
                <span>{row.unit_symbol ?? row.unit ?? '-'}</span>
            )
        },
        {
            name: 'Estado',
            selector: (row: any) => row?.is_active,
            sortable: true,
            sortField: 'is_active',
            width: '100px',
            cell: (row: ServiceDataForm) => (
                <ServiceSwitchChange
                    checkValue={row.is_active ?? true}
                    serviceId={row.id ?? -1}
                    callbackSuccess={callbackSuccess}
                />
            )
        },
        {
            name: 'Acciones',
            selector: (row: any) => row.id,
            sortable: false,
            width: '150px',
            cell: (row: ServiceDataForm) => (
                <div className="">
                    {edit && (
                        <ButtonTableAction
                            callbackFunction={() => edit(row.id ?? -1)}
                            classIcon="mdi mdi-pencil"
                            colorIcon="text-warning"
                            errorMessage="No se puede editar este registro."
                            title="Editar"
                        />
                    )}
                    {destroy && (
                        <ButtonTableAction
                            callbackFunction={() => destroy(row.id ?? -1)}
                            classIcon="mdi mdi-delete"
                            colorIcon="text-danger"
                            errorMessage="No se puede eliminar este registro."
                            title="Eliminar"
                        />
                    )}
                </div>
            )
        }
    ];

    return (
        <>
            <div className="row mt-3">
                <div className="col">
                    <ServiceFilter
                        filter={filter}
                        setFilter={setFilter}
                        onSearch={callbackSuccess}
                    />
                </div>
            </div>
            <div className="row mt-3">
                <div className="col">
                    <DefaultDataTable
                    columns={columns}
                    data={services}
                    progressPending={loading}
                    paginationTotalRows={totalRows}
                    filter={filter}
                    setFilter={setFilter}
                />
            </div>
        </div>
        </>
    );
};

export default ServiceDataTable;
